import { useState, useRef, useEffect } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Send, FileText, Mail, MessageSquare, Plus } from "lucide-react";

const tabs = [
  { key: "chat", label: "Tax Chat", icon: MessageSquare },
  { key: "summarize", label: "Summarize Doc", icon: FileText },
  { key: "email", label: "Draft Email", icon: Mail },
];

const samplePrompts = [
  "What is the due date for GSTR-3B for monthly filers?",
  "Explain section 80C deductions for FY 2025-26.",
  "What documents are needed for ITR-3 filing?",
  "How is TDS on rent (194I) computed?",
];

export default function AIAssistant() {
  const [tab, setTab] = useState("chat");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  // Summarize state
  const [docText, setDocText] = useState("");
  const [summary, setSummary] = useState("");
  const [busySum, setBusySum] = useState(false);

  // Email state
  const [emailForm, setEmailForm] = useState({ purpose: "", client_name: "", context: "" });
  const [emailOut, setEmailOut] = useState("");
  const [busyEmail, setBusyEmail] = useState(false);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const newChat = () => {
    setMessages([]);
    setSessionId(null);
  };

  const sendChat = async (msg) => {
    const text = (msg ?? input).trim();
    if (!text || streaming) return;
    setInput("");
    setMessages(m => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const token = localStorage.getItem("taxinpho_token");
      const resp = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      if (!resp.ok) throw new Error("Stream failed");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n\n");
        buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.type === "session") setSessionId(data.session_id);
            else if (data.type === "delta") {
              setMessages(m => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + data.content };
                return copy;
              });
            } else if (data.type === "error") {
              toast.error(data.message);
            }
          } catch (e) { /* ignore */ }
        }
      }
    } catch (e) {
      toast.error("AI request failed");
    } finally {
      setStreaming(false);
    }
  };

  const summarize = async () => {
    if (!docText.trim()) return;
    setBusySum(true);
    setSummary("");
    try {
      const { data } = await api.post("/ai/summarize", { text: docText });
      setSummary(data.summary);
    } catch (e) {
      toast.error("Summarization failed");
    } finally {
      setBusySum(false);
    }
  };

  const draftEmail = async (e) => {
    e.preventDefault();
    setBusyEmail(true);
    setEmailOut("");
    try {
      const { data } = await api.post("/ai/draft-email", emailForm);
      setEmailOut(data.email);
    } catch (e) {
      toast.error("Email draft failed");
    } finally {
      setBusyEmail(false);
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-[1600px]">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Sparkles size={14} className="text-[#002FA7]" />
            <p className="label-caps text-[#002FA7]">Intelligence</p>
          </div>
          <h1 className="font-display font-black text-4xl tracking-tighter">AI Assistant</h1>
          <p className="text-zinc-600 mt-2">Powered by GPT-5.2 · trained for Indian tax & compliance.</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-zinc-200">
        {tabs.map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`ai-tab-${t.key}`}
              className={`px-4 py-3 text-sm font-semibold flex items-center gap-2 border-b-2 -mb-px transition-colors ${tab === t.key ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-zinc-600 hover:text-zinc-900"}`}
            >
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Chat */}
      {tab === "chat" && (
        <div className="bg-white border border-zinc-200 flex flex-col" style={{ height: "calc(100vh - 280px)", minHeight: "500px" }}>
          <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
            <div className="label-caps">Tax Chat Session</div>
            <button onClick={newChat} className="text-xs font-semibold border border-zinc-300 px-3 py-1 hover:bg-zinc-50 flex items-center gap-1" data-testid="new-chat">
              <Plus size={12} /> New Chat
            </button>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 scrollbar-thin">
            {messages.length === 0 && (
              <div className="text-center max-w-md mx-auto py-12">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-[#EFF4FF] mb-4">
                  <Sparkles size={20} className="text-[#002FA7]" />
                </div>
                <h3 className="font-display font-bold text-lg mb-2">Ask about Indian tax & compliance</h3>
                <p className="text-sm text-zinc-600 mb-6">GST · ITR · TDS · ROC · Audit. Start with a sample below.</p>
                <div className="space-y-2">
                  {samplePrompts.map(p => (
                    <button key={p} onClick={() => sendChat(p)} className="block w-full text-left text-sm border border-zinc-200 px-4 py-2.5 hover:border-[#002FA7] hover:bg-[#EFF4FF] transition-colors">
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`mb-4 flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-zinc-900 text-white" : "bg-[#EFF4FF] border border-[#002FA7]/20 text-zinc-900"}`}>
                  {m.content || (
                    <span className="font-mono text-xs">
                      <span className="ai-loading-dot">.</span><span className="ai-loading-dot">.</span><span className="ai-loading-dot">.</span>
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
          <form onSubmit={(e) => { e.preventDefault(); sendChat(); }} className="border-t border-zinc-200 p-3 flex gap-2">
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder="Ask anything about Indian tax..."
              data-testid="chat-input"
              disabled={streaming}
              className="flex-1 border border-zinc-300 px-3 py-2.5 focus:outline-none focus:border-zinc-900"
            />
            <button type="submit" disabled={streaming || !input.trim()} data-testid="chat-send" className="bg-[#002FA7] text-white px-4 py-2.5 font-semibold flex items-center gap-2 hover:bg-blue-800 disabled:opacity-50">
              <Send size={14} /> {streaming ? "..." : "Send"}
            </button>
          </form>
        </div>
      )}

      {/* Summarize */}
      {tab === "summarize" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white border border-zinc-200 p-6">
            <h3 className="font-display font-bold text-lg mb-2">Paste document text</h3>
            <p className="text-xs text-zinc-600 mb-4">Invoices, GST notices, audit letters, agreements — AI extracts key details.</p>
            <textarea
              value={docText}
              onChange={e => setDocText(e.target.value)}
              data-testid="doc-input"
              rows="14"
              placeholder="Paste invoice text, notice, or any document here..."
              className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-zinc-900 font-mono"
            />
            <button onClick={summarize} disabled={busySum || !docText.trim()} data-testid="summarize-btn" className="mt-4 bg-[#002FA7] text-white px-4 py-2 font-semibold flex items-center gap-2 hover:bg-blue-800 disabled:opacity-50">
              {busySum ? "Analysing..." : <><Sparkles size={14} /> Summarize</>}
            </button>
          </div>
          <div className="bg-[#EFF4FF] border border-[#002FA7] p-6">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles size={14} className="text-[#002FA7]" />
              <p className="label-caps text-[#002FA7]">AI Summary</p>
            </div>
            <h3 className="font-display font-bold text-lg mb-4">Key details extracted</h3>
            {summary ? (
              <pre className="text-sm whitespace-pre-wrap font-sans text-zinc-900 leading-relaxed">{summary}</pre>
            ) : (
              <p className="text-sm text-zinc-600 font-mono">[ summary will appear here ]</p>
            )}
          </div>
        </div>
      )}

      {/* Email */}
      {tab === "email" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <form onSubmit={draftEmail} className="bg-white border border-zinc-200 p-6 space-y-4">
            <h3 className="font-display font-bold text-lg mb-2">Email details</h3>
            <div>
              <label className="label-caps block mb-2">Purpose *</label>
              <input required value={emailForm.purpose} onChange={e => setEmailForm({ ...emailForm, purpose: e.target.value })} placeholder="e.g. Payment reminder, GST filing deadline notice" data-testid="email-purpose" className="w-full border border-zinc-300 px-3 py-2 focus:outline-none focus:border-zinc-900" />
            </div>
            <div>
              <label className="label-caps block mb-2">Client Name *</label>
              <input required value={emailForm.client_name} onChange={e => setEmailForm({ ...emailForm, client_name: e.target.value })} placeholder="Mr. Sharma / ABC Pvt Ltd" className="w-full border border-zinc-300 px-3 py-2 focus:outline-none focus:border-zinc-900" />
            </div>
            <div>
              <label className="label-caps block mb-2">Context (optional)</label>
              <textarea rows="5" value={emailForm.context} onChange={e => setEmailForm({ ...emailForm, context: e.target.value })} placeholder="Outstanding ₹50,000 since Oct 2025..." className="w-full border border-zinc-300 px-3 py-2 focus:outline-none focus:border-zinc-900" />
            </div>
            <button type="submit" disabled={busyEmail} data-testid="draft-email-btn" className="bg-[#002FA7] text-white px-4 py-2 font-semibold flex items-center gap-2 hover:bg-blue-800 disabled:opacity-50">
              {busyEmail ? "Drafting..." : <><Sparkles size={14} /> Draft Email</>}
            </button>
          </form>
          <div className="bg-[#EFF4FF] border border-[#002FA7] p-6">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Sparkles size={14} className="text-[#002FA7]" />
                <p className="label-caps text-[#002FA7]">Drafted Email</p>
              </div>
              {emailOut && (
                <button onClick={() => { navigator.clipboard.writeText(emailOut); toast.success("Copied"); }} className="text-xs font-semibold border border-zinc-300 bg-white px-2 py-1 hover:bg-zinc-50">Copy</button>
              )}
            </div>
            {emailOut ? (
              <pre className="text-sm whitespace-pre-wrap font-sans text-zinc-900 leading-relaxed">{emailOut}</pre>
            ) : (
              <p className="text-sm text-zinc-600 font-mono">[ email will appear here ]</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
